import React, { useState, useMemo } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import { getServices, deleteService } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { withSectionProtection } from '../../client/components/ProtectedSection';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import { isAdministrator } from '../../client/lib/platformRole';
import {
  EnhancedActionButton,
  EnhancedIconButton,
  useResponsiveLayout
} from '../../components/ui/enhanced-page-layout';
import { DetailListItem } from '../../components/ui/detail-list-item';
import { Search, Filter, Plus, Eye, Edit, Trash2, Phone, Clock, Star, Award } from 'lucide-react';
import { useFixedPageStack } from '../../client/hooks/useFixedPageStack';

function ServicesPage() {
  const { data: user } = useAuth();
  const showAdminColumns = isAdministrator(user);
  const { selectedLocation, getLocationIdsForQuery } = useLocationContext();
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const routerLocation = useLocation();
  const locSlug = resolveLocationSlug(routerLocation.pathname, params.slug, selectedLocation);
  const deleteServiceFn = useAction(deleteService);
  
  // State for filters and view mode
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedPriceType, setSelectedPriceType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'title' | 'price'>('newest');
  const [showFilters, setShowFilters] = useState(false);
  // Use responsive layout hook
  const { viewMode, setViewMode, isMobile, shouldCollapseSidebar, hideViewSwitcher } = useResponsiveLayout('table');

  // Fetch services using filtered location IDs
  const locationIdsForQuery = getLocationIdsForQuery();
  const { data: services = [], isLoading, error } = useQuery(
    getServices,
    {
      locationIds: locationIdsForQuery,
      filters: {
        category: selectedCategory === 'all' ? undefined : selectedCategory,
        priceType: selectedPriceType === 'all' ? undefined : selectedPriceType,
      }
    },
    {
      enabled: locationIdsForQuery.length > 0
    }
  );

  const hasListContent = !isLoading && !error && services.length > 0;
  const fixedStack = useFixedPageStack({
    secondaryKind: viewMode === 'table' || hasListContent ? 'ref' : 'none',
    secondaryRefDeps: [viewMode, services.length, isLoading, error, showFilters],
  });

  // Filter and sort services
  const filteredServices = useMemo(() => {
    let filtered = services as any[];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter((service: any) =>
        service.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.metadata?.experience?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        filtered = [...filtered].sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        filtered = [...filtered].sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case 'title':
        filtered = [...filtered].sort((a: any, b: any) => a.title.localeCompare(b.title));
        break;
      case 'price':
        filtered = [...filtered].sort((a: any, b: any) => (b.metadata?.price || 0) - (a.metadata?.price || 0));
        break;
    }

    return filtered;
  }, [services, searchTerm, sortBy]);

  const handleDelete = async (serviceId: string) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await deleteServiceFn({ itemId: serviceId });
        // The query will automatically refetch due to Wasp's reactivity
      } catch (error) {
        console.error('Error deleting service:', error);
        alert('Error deleting service. Please try again.');
      }
    }
  };

  if (!selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Location Selected</h2>
            <p className="text-gray-600">Please select a location to view services.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getCategoryColor = (category: string) => {
    const colors = {
      'TUTORING': 'bg-primary/10 text-primary border border-primary/20',
      'CLEANING': 'bg-success/10 text-success border border-success/20',
      'REPAIR': 'bg-secondary/10 text-secondary border border-secondary/20',
      'PERSONAL_CARE': 'bg-secondary/10 text-secondary border border-secondary/20',
      'TRANSPORTATION': 'bg-accent/10 text-accent border border-accent/20',
      'EVENT_PLANNING': 'bg-warning/10 text-warning border border-warning/20',
      'PHOTOGRAPHY': 'bg-destructive/10 text-destructive border border-destructive/20',
      'CONSULTING': 'bg-primary/10 text-primary border border-primary/20',
      'OTHER': 'bg-muted text-muted-foreground border border-border'
    };
    return colors[category as keyof typeof colors] || 'bg-muted text-muted-foreground border border-border';
  };

  const getPriceTypeColor = (priceType: string) => {
    const colors = {
      'HOURLY': 'bg-primary/10 text-primary border border-primary/20',
      'FIXED': 'bg-success/10 text-success border border-success/20',
      'PER_SESSION': 'bg-accent/10 text-accent border border-accent/20',
      'FREE': 'bg-muted text-muted-foreground border border-border'
    };
    return colors[priceType as keyof typeof colors] || 'bg-muted text-muted-foreground border border-border';
  };

  const getAvailabilityColor = (availability: string) => {
    const colors = {
      'AVAILABLE': 'bg-success/10 text-success border border-success/20',
      'LIMITED': 'bg-warning/10 text-warning border border-warning/20',
      'BUSY': 'bg-destructive/10 text-destructive border border-destructive/20'
    };
    return colors[availability as keyof typeof colors] || 'bg-muted text-muted-foreground border border-border';
  };

  return (
    <DashboardLayout 
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <div className={fixedStack.pageContainerClassName} style={fixedStack.pageContainerStyle}>
        {/* Fixed Main Header */}
        <div
          ref={fixedStack.pageHeaderRef}
          className="fixed top-[var(--header-height)] left-0 lg:left-[var(--sidebar-width)] right-0 z-20 bg-white border-b border-gray-200 transition-all duration-300"
        >
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Services</h1>
                <p className="text-sm text-gray-600 mt-1">Discover and offer services at {selectedLocation.name} ({filteredServices.length} services)</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    showFilters
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  <span className="hidden lg:inline">Filters</span>
                </button>
                {user && (
                  <EnhancedActionButton
                onClick={() => navigate(locationSectionPath(locSlug, 'services', 'add'))}
                icon={Plus}
                label={<span className="hidden lg:inline">Offer Service</span>}
              />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Table Header (Conditional) */}
        {viewMode === 'table' && (
          <div
            ref={fixedStack.secondaryStackRef}
            className={`fixed ${fixedStack.secondaryFixedTopClass} left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-sidebar-hover border-b border-border transition-all duration-300`}
          >
            <div className="px-6 py-3 space-y-3">
              {/* Column Headers */}
              <div className={`grid gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default ${showAdminColumns ? 'grid-cols-[2fr,1.5fr,1fr,1fr,1.5fr,1fr,0.5fr]' : 'grid-cols-[2fr,1.5fr]'}`}>
                <div className="text-left pl-3">Service</div>
                <div className="text-left pl-3">Category</div>
                {showAdminColumns && (
                  <>
                    <div className="text-left pl-3">Price Type</div>
                    <div className="text-left pl-3">Price</div>
                    <div className="text-left pl-3">Availability</div>
                    <div className="text-left pl-3">Provider</div>
                    <div className="text-left pl-3">Actions</div>
                  </>
                )}
              </div>

              {/* Column Filters */}
              {showFilters && <div className={`grid gap-4 ${showAdminColumns ? 'grid-cols-[2fr,1.5fr,1fr,1fr,1.5fr,1fr,0.5fr]' : 'grid-cols-[2fr,1.5fr]'}`}>
                <div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search services..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All</option>
                    <option value="TUTORING">Tutoring</option>
                    <option value="CLEANING">Cleaning</option>
                    <option value="REPAIR">Repair</option>
                    <option value="PERSONAL_CARE">Personal Care</option>
                    <option value="TRANSPORTATION">Transportation</option>
                    <option value="EVENT_PLANNING">Event Planning</option>
                    <option value="PHOTOGRAPHY">Photography</option>
                    <option value="CONSULTING">Consulting</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                {showAdminColumns && (
                  <>
                    <div>
                      <select
                        value={selectedPriceType}
                        onChange={(e) => setSelectedPriceType(e.target.value)}
                        className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All</option>
                        <option value="HOURLY">Hourly</option>
                        <option value="FIXED">Fixed</option>
                        <option value="PER_SESSION">Per Session</option>
                        <option value="FREE">Free</option>
                      </select>
                    </div>
                    <div>
                      <select
                        value={sortBy === 'price' ? 'price' : 'all'}
                        onChange={(e) => setSortBy(e.target.value === 'price' ? 'price' : 'newest')}
                        className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All</option>
                        <option value="price">High-Low</option>
                      </select>
                    </div>
                    <div>
                      <select
                        className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All</option>
                        <option value="AVAILABLE">Available</option>
                        <option value="LIMITED">Limited</option>
                        <option value="BUSY">Busy</option>
                      </select>
                    </div>
                    <div>
                      <select
                        value={sortBy === 'newest' || sortBy === 'oldest' || sortBy === 'title' ? sortBy : 'newest'}
                        onChange={(e) => setSortBy(e.target.value as any)}
                        className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="newest">Newest</option>
                        <option value="oldest">Oldest</option>
                        <option value="title">A-Z</option>
                      </select>
                    </div>
                    <div>
                      {/* Actions column - no filter needed */}
                    </div>
                  </>
                )}
              </div>}
            </div>
          </div>
        )}

        {/* Fixed Filters for non-table views */}
        {!isLoading && !error && services.length > 0 && viewMode !== 'table' && (
          <div
            ref={fixedStack.secondaryStackRef}
            className={`fixed ${fixedStack.secondaryFixedTopClass} left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-white border-b border-gray-200 transition-all duration-300`}
          >
            <div className="px-4 py-3">
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search services..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Category Filter */}
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  <option value="TUTORING">Tutoring</option>
                  <option value="CLEANING">Cleaning</option>
                  <option value="REPAIR">Repair</option>
                  <option value="PERSONAL_CARE">Personal Care</option>
                  <option value="TRANSPORTATION">Transportation</option>
                  <option value="EVENT_PLANNING">Event Planning</option>
                  <option value="PHOTOGRAPHY">Photography</option>
                  <option value="CONSULTING">Consulting</option>
                  <option value="OTHER">Other</option>
                </select>

                {/* Price Type Filter */}
                <select
                  value={selectedPriceType}
                  onChange={(e) => setSelectedPriceType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Price Types</option>
                  <option value="HOURLY">Hourly</option>
                  <option value="FIXED">Fixed</option>
                  <option value="PER_SESSION">Per Session</option>
                  <option value="FREE">Free</option>
                </select>
                
                {/* Sort Filter */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="title">Title A-Z</option>
                  <option value="price">Price High-Low</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Scrollable Content */}
        <div
          className={fixedStack.scrollContainerClassName}
          style={fixedStack.scrollContainerStyle}
        >
          <div className="w-full">

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading services...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Award className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Services</h3>
                  <p className="text-gray-600">Please try again later.</p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && filteredServices.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Services Found</h3>
                  <p className="text-gray-600">
                    {searchTerm || selectedCategory !== 'all' || selectedPriceType !== 'all'
                      ? 'Try adjusting your search or filters to find what you\'re looking for.' 
                      : 'No services have been offered in this location yet.'
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Content */}
            {!isLoading && !error && filteredServices.length > 0 && (
          <>
            {/* Cards View - Use enhanced layout with padding */}
            {viewMode === 'cards' && (
              <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredServices.map((service) => (
                    <div
                      key={service.id}
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => navigate(locationSectionPath(locSlug, 'services', service.id))}
                    >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(service.metadata?.category)}`}>
                            {service.metadata?.category?.replace('_', ' ')}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded ${getPriceTypeColor(service.metadata?.priceType)}`}>
                            {service.metadata?.priceType?.replace('_', ' ')}
                          </span>
                        </div>
                        {service.metadata?.price && service.metadata.priceType !== 'FREE' && (
                          <div className="text-lg font-bold text-green-600">
                            ${service.metadata.price}
                          </div>
                        )}
                        {service.metadata?.priceType === 'FREE' && (
                          <div className="text-lg font-bold text-gray-600">
                            Free
                          </div>
                        )}
                      </div>
                      
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                        {service.title}
                      </h3>
                      
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {service.description}
                      </p>

                      {service.metadata?.duration && (
                          <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                          <Clock className="w-3 h-3" />
                          {service.metadata.duration}
                        </div>
                      )}

                      {service.metadata?.experience && (
                          <div className="flex items-center gap-1 text-sm text-gray-500 mb-4">
                          <Star className="w-3 h-3" />
                          {service.metadata.experience}
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-1">
                          <div className="text-sm text-gray-500">
                            by {service.author.name || service.author.email}
                          </div>
                        </div>
                          <div
                            className="flex items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <EnhancedIconButton
                              onClick={() => navigate(locationSectionPath(locSlug, 'services', service.id))}
                              icon={Eye}
                              title="View details"
                            />
                          {user && service.authorId === user.id && (
                            <>
                                <EnhancedIconButton
                                  onClick={() => navigate(locationSectionPath(locSlug, 'services', service.id, 'edit'))}
                                  icon={Edit}
                                  title="Edit service"
                                />
                                <EnhancedIconButton
                                onClick={() => handleDelete(service.id)}
                                  icon={Trash2}
                                  title="Delete service"
                                  variant="danger"
                                />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              </div>
            )}

            {/* Detail List View */}
            {viewMode === 'list' && (
              <div className="pb-2">
                {filteredServices.map((service) => (
                  <DetailListItem
                    key={service.id}
                    title={service.title}
                    description={service.description}
                    website={(service.metadata as any)?.website ?? null}
                    onSelect={() => navigate(locationSectionPath(locSlug, 'services', service.id))}
                    meta={
                      <>
                        {service.metadata?.category && (
                          <span className={`px-2 py-0.5 rounded ${getCategoryColor(service.metadata.category)}`}>
                            {service.metadata.category.replace('_', ' ')}
                          </span>
                        )}
                        {service.metadata?.priceType && (
                          <span className={`px-2 py-0.5 rounded ${getPriceTypeColor(service.metadata.priceType)}`}>
                            {service.metadata.priceType.replace('_', ' ')}
                          </span>
                        )}
                        {service.metadata?.duration && (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {service.metadata.duration}
                          </span>
                        )}
                        {service.metadata?.price != null && service.metadata.priceType !== 'FREE' && (
                          <span className="font-medium text-green-600">${service.metadata.price}</span>
                        )}
                        {service.metadata?.priceType === 'FREE' && (
                          <span className="font-medium">Free</span>
                        )}
                      </>
                    }
                    trailing={
                      <>
                        <EnhancedIconButton
                          onClick={() => navigate(locationSectionPath(locSlug, 'services', service.id))}
                          icon={Eye}
                          title="View details"
                        />
                        {user && service.authorId === user.id && (
                          <>
                            <EnhancedIconButton
                              onClick={() => navigate(locationSectionPath(locSlug, 'services', service.id, 'edit'))}
                              icon={Edit}
                              title="Edit service"
                            />
                            <EnhancedIconButton
                              onClick={() => handleDelete(service.id)}
                              icon={Trash2}
                              title="Delete service"
                              variant="danger"
                            />
                          </>
                        )}
                      </>
                    }
                  />
                ))}
              </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
              <div className="w-full">
                {/* Table Body - No internal scroll */}
                <div className="divide-y divide-gray-200">
                  {filteredServices.map((service) => (
                    <div
                      key={service.id}
                      className="px-6 py-3 hover:bg-hover transition-colors cursor-pointer"
                      onClick={() => navigate(locationSectionPath(locSlug, 'services', service.id))}
                    >
                      <div className={`grid gap-4 items-center ${showAdminColumns ? 'grid-cols-[2fr,1.5fr,1fr,1fr,1.5fr,1fr,0.5fr]' : 'grid-cols-[2fr,1.5fr]'}`}>
                        <div className="text-left pl-3">
                          <div className="text-sm font-medium text-gray-900">
                            {service.title}
                          </div>
                          <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {service.description}
                          </div>
                        </div>
                        <div className="text-left pl-3">
                          <span className="text-sm text-gray-600">
                            {service.metadata?.category?.replace('_', ' ')}
                          </span>
                        </div>
                        {showAdminColumns && (
                          <>
                            <div className="text-left pl-3">
                              <span className="text-sm text-gray-600">
                                {service.metadata?.priceType?.replace('_', ' ')}
                              </span>
                            </div>
                            <div className="text-left pl-3">
                              <span className="text-sm text-gray-600">
                                {service.metadata?.priceType === 'FREE' ? 'Free' : service.metadata?.price ? `$${service.metadata.price}` : '-'}
                              </span>
                            </div>
                            <div className="text-left pl-3">
                              <span className="text-sm text-gray-600">
                                Available
                              </span>
                            </div>
                            <div className="text-left pl-3">
                              <span className="text-sm text-gray-500">
                                {service.author.username || service.author.name || service.author.email}
                              </span>
                            </div>
                            <div className="text-left pl-3">
                              <div
                                className="flex items-center gap-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <EnhancedIconButton
                                  onClick={() => navigate(locationSectionPath(locSlug, 'services', service.id))}
                                  icon={Eye}
                                  title="View details"
                                />
                                {user && service.authorId === user.id && (
                                  <>
                                    <EnhancedIconButton
                                      onClick={() => navigate(locationSectionPath(locSlug, 'services', service.id, 'edit'))}
                                      icon={Edit}
                                      title="Edit service"
                                    />
                                    <EnhancedIconButton
                                      onClick={() => handleDelete(service.id)}
                                      icon={Trash2}
                                      title="Delete service"
                                      variant="danger"
                                    />
                                  </>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default withSectionProtection(ServicesPage, 'services');



